import React from 'react';
import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@roger/ui-components';
import { ChevronDown } from 'lucide-react';
import type {
  DemoClinicIdentity,
  KinnserPullSummary,
} from '../../services/api/types.gen';
import { ClinicIdentityFields } from './ClinicIdentityFields';
import type { ResolvedDemoContent } from './demoContent';
import { DemoFormSection } from './DemoFormSection';
import { PulledPayers } from './PulledPayers';
import { excludedInPull, pulledCount } from './kinnserSourceCount';

/**
 * Whose data this sandbox shows, one box per kind.
 *
 * Four questions rather than one, because the last pull can answer some and not
 * others. A kind the pull has no rows of falls back to invented at build time
 * whatever the box says, which is quiet -- so that box offers no choice and says
 * what is missing instead of letting somebody believe the demo will show the
 * agency's own.
 *
 * A two-state control rather than a checkbox, because "invented" is a real answer
 * and not the absence of one. An unticked box said nothing about what the sandbox
 * would show; "Invented" says it.
 *
 * Each kind is a `DemoFormSection` with its toggle in the header, the same block
 * and the same place every other group of settings on this page uses -- so four
 * boxes among the rest read as four of the same thing rather than a fifth kind of
 * furniture. The body says what the choice means, because the toggle alone does
 * not tell you which demo you are looking at.
 */

type Kind = keyof ResolvedDemoContent['kinnser_sources'];

const KINDS: { key: Kind; label: string; lower: string }[] = [
  { key: 'clinics', label: 'Clinics', lower: 'clinics' },
  { key: 'payers', label: 'Payers', lower: 'payers' },
  { key: 'physicians', label: 'Physicians', lower: 'physicians' },
  { key: 'staff', label: 'Staff', lower: 'staff' },
];

export const KinnserSourcesSection: React.FC<{
  agencyId: string;
  sources: ResolvedDemoContent['kinnser_sources'];
  /** The demo environment's own last pull; null when never pulled. */
  pull: KinnserPullSummary | null | undefined;
  /** The Kinnser clinic ids this build leaves out. */
  excluded: ReadonlySet<string>;
  onExcludedChange: (clinicId: string, leaveOut: boolean) => void;
  onChange: (sources: ResolvedDemoContent['kinnser_sources']) => void;
  /** Typed NPI, EIN, CCN / PTAN and address, by Kinnser clinic id. */
  identities: Record<string, DemoClinicIdentity>;
  onIdentitiesChange: (identities: Record<string, DemoClinicIdentity>) => void;
}> = ({
  agencyId,
  sources,
  pull,
  excluded,
  onExcludedChange,
  onChange,
  identities,
  onIdentitiesChange,
}) => (
  /* Clinics and Payers take the full width, because they list their rows;
     Physicians and Staff are a count and a switch, so they pair up. One
     across on a phone. */
  <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
    {KINDS.map(kind => {
      const count = pulledCount(pull, kind.key);
      // The other three live inside a clinic in the pull, so an invented set of
      // clinics has nowhere to hang the agency's real payers, physicians or
      // staff: the build drops them all. Said here, where the choice is made,
      // rather than leaving three boxes promising data no run will make.
      const clinicsInvented = kind.key !== 'clinics' && !sources.clinics;
      const real = count > 0 && !clinicsInvented && sources[kind.key];
      return (
        <div
          key={kind.key}
          className={
            kind.key === 'clinics' || kind.key === 'payers'
              ? 'lg:col-span-2'
              : undefined
          }
        >
          <DemoFormSection
            title={kind.label}
            note={
              count === 0
                ? 'None synced'
                : clinicsInvented
                  ? 'Needs real clinics'
                  : kind.key === 'clinics' && real
                    ? `${count - excludedInPull(pull, excluded)} of ${count} built`
                    : `${count.toLocaleString()} in the last sync`
            }
            /* In the header, where every other box on this page puts its
             controls. */
            actions={
              count === 0 || clinicsInvented ? undefined : (
                <div
                  role='group'
                  aria-label={`${kind.label} data`}
                  className='flex overflow-hidden rounded-md border'
                >
                  {[
                    { on: false, label: 'Invented' },
                    { on: true, label: 'Real' },
                  ].map(choice => (
                    <button
                      key={choice.label}
                      type='button'
                      aria-pressed={real === choice.on}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        real === choice.on
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                      onClick={() =>
                        onChange({ ...sources, [kind.key]: choice.on })
                      }
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              )
            }
          >
            {/* What the choice means, rather than the choice again. A build that
              invents these and a build that shows the agency's own are different
              demos, and the toggle alone does not say which you are looking at. */}
            <p className='text-xs text-muted-foreground'>
              {count === 0
                ? `A build invents ${kind.lower} until a sync finds some.`
                : clinicsInvented
                  ? `The sandbox invents ${kind.lower}: they belong to a clinic, and this one’s clinics are invented.`
                  : real
                    ? `The sandbox shows the agency’s ${kind.lower} from Kinnser.`
                    : `The sandbox invents ${kind.lower}, ignoring the sync.`}
            </p>

            {/* Which payers, by name, so the pull can be checked before Run.
              Per built clinic, because each carries its own list. */}
            {kind.key === 'payers' && real && (
              <PulledPayers
                agencyId={agencyId}
                pull={pull}
                excluded={excluded}
              />
            )}

            {/* Which clinics, at a finer grain than the toggle above, and what
              each is built with. Only when the sandbox is taking Kinnser's
              clinics: an invented one has no Kinnser id to keep values under.
              A clinic left out shows just its box, since nothing is built; a
              built one's fields stay closed until its name is clicked. */}
            {kind.key === 'clinics' && real && (
              <div className='divide-y border-t'>
                {(pull?.clinic_list ?? []).map(clinic => {
                  const included = !excluded.has(clinic.clinic_id);
                  return (
                    <Collapsible
                      key={clinic.clinic_id}
                      disabled={!included}
                      className='py-3'
                    >
                      <div className='flex items-center gap-2'>
                        <Checkbox
                          aria-label={clinic.name}
                          checked={included}
                          onCheckedChange={checked =>
                            onExcludedChange(clinic.clinic_id, checked !== true)
                          }
                        />
                        <CollapsibleTrigger className='group flex min-w-0 items-center gap-1 text-left text-sm font-medium'>
                          <span className='truncate'>{clinic.name}</span>
                          {included && (
                            <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180' />
                          )}
                        </CollapsibleTrigger>
                      </div>
                      {included && (
                        <CollapsibleContent className='pt-2'>
                          <ClinicIdentityFields
                            clinic={clinic}
                            identities={identities}
                            onChange={onIdentitiesChange}
                          />
                        </CollapsibleContent>
                      )}
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </DemoFormSection>
        </div>
      );
    })}
  </div>
);

export default KinnserSourcesSection;
